import React, { useEffect, useState } from "react";
import { THEME } from "./state";

// Course metadata: id → {label, storageKey, href, accent, totalModules}
const COURSES = [
  { id: "beginner",    label: "Beginner & Intermediate", href: "/beginner",    accent: "#01696f", storageKey: "flexx_v5",     totalModules: 36 },
  { id: "advanced",    label: "Advanced Program",         href: "/advanced",    accent: "#C9A227", storageKey: "flexx_adv_v5", totalModules: 24 },
  { id: "cfd",         label: "CFD Mastery",              href: "/cfd",         accent: "#6daa45", storageKey: "flexx_cfd_v5", totalModules: 21 },
  { id: "commodities", label: "Commodities & Futures",    href: "/commodities", accent: "#a12c7b", storageKey: "flexx_com_v5", totalModules: 19 },
  { id: "roadmap",     label: "3-Month Roadmap",          href: "/roadmap",     accent: "#4A90D9", storageKey: "flexx_rm_v5",  totalModules: 21 },
];

function readCourseProgress(storageKey, totalModules) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { completed: 0, total: totalModules, percent: 0 };
    const parsed = JSON.parse(raw);
    const completedMap = parsed?.completed || {};
    const completedCount = Object.values(completedMap).filter(Boolean).length;
    const percent = totalModules > 0 ? Math.round((completedCount / totalModules) * 100) : 0;
    return { completed: completedCount, total: totalModules, percent };
  } catch {
    return { completed: 0, total: totalModules, percent: 0 };
  }
}

export default function CourseProgressRings() {
  const [rows, setRows] = useState(() => COURSES.map((c) => ({ ...c, progress: readCourseProgress(c.storageKey, c.totalModules) })));

  useEffect(() => {
    const refresh = () => setRows(COURSES.map((c) => ({ ...c, progress: readCourseProgress(c.storageKey, c.totalModules) })));
    // Refresh on sync restore + on our own course-XP bridge ticks
    window.addEventListener("flexx:restored", refresh);
    window.addEventListener("flexx:course-progress", refresh);
    // Also listen for storage events (from other tabs)
    window.addEventListener("storage", refresh);
    // And poll lightly for same-tab updates
    const interval = setInterval(refresh, 4000);
    return () => {
      window.removeEventListener("flexx:restored", refresh);
      window.removeEventListener("flexx:course-progress", refresh);
      window.removeEventListener("storage", refresh);
      clearInterval(interval);
    };
  }, []);

  const totalCompleted = rows.reduce((acc, r) => acc + (r.progress?.completed || 0), 0);
  const totalLessons = rows.reduce((acc, r) => acc + (r.progress?.total || 0), 0);
  const overall = totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0;

  return (
    <div className="flexx-card" data-testid="course-progress-rings">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>COURSE PROGRESS</div>
          <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700, marginTop: 4 }}>
            All Enrolled Courses
          </div>
        </div>
        <div style={{ textAlign: "right" }} data-testid="course-progress-overall">
          <div style={{ color: THEME.gold, fontWeight: 800, fontSize: 20 }}>{overall}%</div>
          <div style={{ color: THEME.mute, fontSize: 11 }}>{totalCompleted} / {totalLessons} lessons</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
        {rows.map((r) => (
          <a
            key={r.id}
            href={r.href}
            data-testid={`course-ring-${r.id}`}
            style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "10px 4px", borderRadius: 10, border: `1px solid ${THEME.border}`, background: "#13161c", transition: "all 0.2s ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = r.accent + "80"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = THEME.border; }}
          >
            <Ring percent={r.progress.percent} accent={r.accent} />
            <div style={{ color: THEME.text, fontSize: 11, fontWeight: 600, textAlign: "center", lineHeight: 1.3 }}>
              {r.label}
            </div>
            <div style={{ color: THEME.mute, fontSize: 10 }}>
              {r.progress.completed} / {r.progress.total}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function Ring({ percent, accent, size = 64 }) {
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#2a2e36" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        color: "#f0f0f0", fontWeight: 800, fontSize: 13,
      }}>
        {Math.round(percent)}%
      </div>
    </div>
  );
}
